import React, { useState, useEffect } from "react";
import LogoWithLanguage from "../../../components/logo-with-language/LogoWithLanguage";
import { classNames, onDirectUpload } from "../../../helpers";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiResponse } from "../../../api/apiResponse";
import { createLearner } from "../../../api/endpoints/learner/learner";
import toast from "react-hot-toast";
import ContainerMd from "../../../components/Layouts/ContainerMd";
import VideoRecorder from "../../../components/recorder/VideoRecorder";
import AudioRecorder from "../../../components/recorder/AudioRecorder";
type RecordingMode = "audio" | "video";
export default function ReadingRecorder() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams();
  const LocalBasePath = "page.readingRecorder";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [isUploading, setIsUploading] = useState(false);
  const [apiData, setApiData] = useState({});
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enableVideo, setEnableVideo] = useState(true);
  const [recordingMode, setRecordingMode] = useState<RecordingMode>();

  useEffect(() => {
    if (params.type === "video") {
      setEnableVideo(true);
    } else {
      setEnableVideo(false);
    }

    setApiData({
      full_name: searchParams.get("full_name"),
      first_name: searchParams.get("first_name"),
      middle_name: searchParams.get("middle_name"),
      last_name: searchParams.get("last_name"),
      phone_number: searchParams.get("phone_number"),
      gender: searchParams.get("gender"),
      program_name: searchParams.get("program_name"),
      telawah_id: searchParams.get("telawah_id"),
      email: searchParams.get("email"),
      timezone: searchParams.get("timezone"),
      submission: searchParams.get("submission"),
    });
  }, [params.type, searchParams]);

  const handleRecordingComplete = (blobUrl: string) => {
    // This function will be called from the preview page after user clicks submit
    setMediaBlobUrl(blobUrl);
    uploadRecording(blobUrl);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    toast.error(errorMessage);
  };

  // Store API data in localStorage for the preview page
  useEffect(() => {
    if (Object.keys(apiData).length > 0) {
      localStorage.setItem('recordingApiData', JSON.stringify(apiData));
    }
  }, [apiData]);


  const uploadRecording = async (blobUrl: string) => {
    if (!blobUrl) return;

    try {
      setIsUploading(true);
      const response = await fetch(blobUrl);
      const blob = await response.blob();

      const file = new File([blob], `recording-${Date.now()}.webm`, {
        type: blob.type,
      });

      onDirectUpload(file, async (signed_id: any) => {
        const response: ApiResponse = await createLearner({
          ...apiData,
          submission: signed_id,
        });

        if (response.isSuccess) {
          toast.success(t("savedSuccessfully"));
          navigate("/learner/success-booking-account", { replace: true });
        } else {
          response.errorMessages.forEach((errorMessage) => {
            toast.error(errorMessage);
          });
        }
      });
    } catch (error) {
      console.error("Error converting blob to file:", error);
      toast.error(t("uploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleModeChange = (mode: RecordingMode) => {
    navigate(`/learner/reading-recorder/${mode}`);
    setRecordingMode(mode);
  };

  return (
    <ContainerMd>
      <div
        className={classNames("w-full flex flex-col md:bg-white md:p-6 h-full")}
      >
        <div className="mb-36 px-4 md:px-0 h-full flex flex-col">
          <div className="w-full flex mb-6 md:flex-row flex-col justify-between items-center">
            <LogoWithLanguage />
          </div>
          <div className="pb-2 border-b border-gray-200">
            <h1 className="text-gray-700 text-3xl font-bold mb-2">
              {t("readingRecording")}
            </h1>
          </div>
          <div className="mt-6 grow">
            <div className="flex flex-col items-center gap-4 h-full">
              {error && (
                <div className="p-4 bg-red-100 rounded-lg text-red-800 w-full max-w-2xl">
                  <p>{error}</p>
                </div>
              )}

              {/* <div className="flex gap-4 mb-4">
                <button
                  onClick={() => handleModeChange("audio")}
                  className={`px-4 py-2 rounded ${
                    recordingMode === "audio"
                      ? "bg-brandblue-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {t("audioOnly")}
                </button>
                <button
                  onClick={() => handleModeChange("video")}
                  className={`px-4 py-2 rounded ${
                    recordingMode === "video"
                      ? "bg-brandblue-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {t("videoAndAudio")}
                </button>
              </div> */}

              {enableVideo ? (
                <VideoRecorder
                  onRecordingComplete={handleRecordingComplete}
                  enableVideo={true}
                  onError={handleError}
                  isUploading={isUploading}
                  mediaBlobUrl={mediaBlobUrl}
                />
              ) : (
                <AudioRecorder
                  onRecordingComplete={handleRecordingComplete}
                  onError={handleError}
                  previewRoute="/learner/preview"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ContainerMd>
  );
}
